import { html } from '@acme/b2b/utils';
import { GridDefinitionFactory } from '@b2b/common/factories/gridDefinition/types';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { CartService, CartServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { ColumnDef, ContentHeaderProps } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IScope, ITimeoutService } from 'angular';

interface PartnerListItem {
  accountCategoryRelation: { categoryName: string };
  company: {
    organizationId: number;
    organizationName: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    city: string;
    state: string;
    category: string;
    reseller: string;
    name: string;
  };
  primaryContact: { contactName: string };
  request: object;
  reseller: { status: string };
}

PartnersListController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  'ApiService',
  'VenueService',
  'HelpersService',
  'CalendarService',
  'GridStateFactory',
  'ViewStateService',
  'GridDefinitionFactory',
  'CartService',
  'ThemesHelperService',
];

function PartnersListController(
  $scope: IScope & { partnersList: TODO },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  ApiService: ApiService,
  VenueService: TODO,
  HelpersService: TODO,
  CalendarService: CalendarService,
  GridStateFactory: GridStateFactory,
  ViewStateService: TODO,
  GridDefinitionFactory: GridDefinitionFactory,
  cartService: CartService,
) {
  const ctrl = {
    model: [] as TODO[],
    categorized: [] as TODO[],
    categories: [] as TODO[],
    searchQuery: ViewStateService.getStateProperty('partners.list', 'searchQuery', ''),
    queryTimer: null as IPromise<void> | null,
    gridState: new GridStateFactory(),
    gridDefinition: new GridDefinitionFactory(),
    isLoaded: false,

    getStatusLabel: VenueService.getStatusLabel,
    selectedCategory: null as TODO | null,
    organizationName: '',
    contactName: '',

    optionsOverride: {
      useAccessibleGrid: true,
      data: 'partnersList.model',
      appScopeProvider: {
        onDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: function (grid, columns, page) {
          ctrl.onDataGridSortChange(grid, columns, page);
        },
        onDblClick: function (item: PartnerListItem) {
          ctrl.onDblClick(item);
        },
        createOrder: function (item: PartnerListItem) {
          ctrl.createOrder(item);
        },
        createCustomEvent: function (item: PartnerListItem) {
          ctrl.createCustomEvent(item);
        },
      },
      columnDefs: [
        {
          field: 'view',
          displayName: 'View',
          width: 50,
          onCellNavClick: function (row, col) {
            ctrl.optionsOverride.appScopeProvider.onDblClick(row);
          },
          cellTemplate: html`
            <div class="ui-grid-cell-contents">
              <a data-ng-click="grid.appScope.onDblClick(row)" role="link">
                View<span class="sr-only"> Details for {{ row.entity.company.name }}</span>
              </a>
            </div>
          `,
        },
        {
          field: 'company.name',
          displayName: 'Organization',
          colId: 'name',
          sort: { direction: 'asc' },
        },
        {
          field: 'primaryContact.contactName',
          displayName: 'Primary Contact',
          colId: 'contactName',
        },
        {
          field: 'company.phoneNumber',
          displayName: 'Phone',
          colId: 'phoneNumber',
        },
        {
          field: 'company.city',
          displayName: 'City',
          colId: 'city',
          enableSorting: false,
        },
        {
          field: 'company.state',
          displayName: 'State',
          colId: 'state',
          enableSorting: false,
        },
        {
          field: 'accountCategoryRelation.categoryName',
          displayName: 'Category',
          colId: 'categoryName',
        },
        {
          field: 'reseller.status',
          displayName: 'Reseller',
          colId: 'status',
          enableSorting: false,
        },
        {
          field: 'company.organizationId',
          displayName: 'Order',
          colId: 'id',
          enableHiding: false,
          width: 120,
          suppressSizeToFit: true,
          cellTemplate: html`
            <div class="ui-grid-cell-contents text-center">
              <div class="btn-group dropdown" uib-dropdown dropdown-append-to-body="true">
                <button
                  id="partners-list-actions-{{ data.company.organizationId }}"
                  class="partners-list-dropdown-action btn btn-primary btn-xs"
                  data-ng-click="(grid.appScope ? grid.appScope : partnersList.optionsOverride.appScopeProvider).createOrder(row.entity)">
                  Transact
                </button>

                <button class="btn btn-primary btn-xs dropdown-toggle" uib-dropdown-toggle>
                  <span class="caret"></span>
                </button>

                <ul
                  class="dropdown-menu dropdown-menu-right"
                  uib-dropdown-menu
                  data-uib-tooltip-placement="bottom"
                  role="menu">
                  <li role="menuitem">
                    <a
                      data-ng-click="(grid.appScope ? grid.appScope : partnersList.optionsOverride.appScopeProvider).createCustomEvent(row.entity)">
                      Create Custom Event
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          `,
        },
      ] as ColumnDef[],
    },

    viewId: '',

    accountsListGridName: $state.current.name + '.grid0',

    init: function () {
      ctrl.viewId = $state.current.name ?? '';

      ctrl.getCategories();

      $scope.$on('DataGrid::UserPreferencesLoaded', function () {
        const props = ctrl.gridDefinition.getGridState();

        ctrl.gridState.updateGridState(props.page, props.pageSize, props.sortField, props.sortDirection);
        ctrl.initData();
      });

      ctrl.gridDefinition.init(
        ctrl.accountsListGridName,
        ctrl.optionsOverride || {},
        ctrl.viewId,
        $state.current.data.enablePreferences,
      );

      $scope.$emit('Application::contentTitleChange', {
        title: 'Organizations',
        buttonRight: {
          permissions: ['ACCT_CREATE'],
          label: 'Create Organization',
          action: () => {
            ctrl.create();
          },
        },
      } as ContentHeaderProps);

      ctrl.addHandlers();

      $scope.$on('destroy', () => {
        if (ctrl.queryTimer) {
          $timeout.cancel(ctrl.queryTimer);
        }
      });
    },

    addHandlers: function () {
      //remember view state
      $scope.$on('pageContent::startLoad', function (event, data) {
        if (data && data.from && data.from.name === 'application.partners.list.default') {
          switch (data.to.name) {
            case 'application.partners.detail':
            case 'application.orders.create':
              break;

            default:
              ViewStateService.resetState('partners.list');
          }
        }
      });
    },

    createOrder: function (item: PartnerListItem) {
      ViewStateService.setState('partners.list', {
        searchQuery: ctrl.searchQuery,
        selectedCategory: ctrl.selectedCategory,
        page: ctrl.gridState.getPropertyValue('page'),
      });

      const accountId = item.company.organizationId;

      ApiService.partners
        .detail(accountId)
        .then((response) => {
          /**
           * Find out which are the minimum fields required to update the cart service's
           * organization to avoid having to fetch its details from the partners list
           */
          const organization = response.data.payload.company;
          cartService.updateOrganization(organization);

          $state.go('application.programs.list');
        })
        .catch(() => {
          console.error('PartnersListController::createOrder', 'Failed to fetch partner details');
        });
    },

    createCustomEvent: function (item: PartnerListItem) {
      ViewStateService.setState('partners.list', {
        searchQuery: ctrl.searchQuery,
        selectedCategory: ctrl.selectedCategory,
        page: ctrl.gridState.getPropertyValue('page'),
      });

      const accountId = item.company.organizationId;
      $state.go('application.orders.create', { accountId });
    },

    onDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
      ctrl.gridState.updateGridState(newPageNumber, newPageSize);

      if (sortField) {
        ctrl.gridState.updateGridState(null, null, sortField, null);
      }
      if (sortDirection) {
        ctrl.gridState.updateGridState(null, null, null, sortDirection);
      }
      ctrl.setDataForGrid();
    },

    onDataGridSortChange: function (grid, columns, page) {
      const data = columns[0];

      ctrl.gridState.updateGridState(page || null, null, data.field, data.sort.direction);
      ctrl.setDataForGrid();
    },

    getCategories: function () {
      ApiService.partners.categories
        .list(HelpersService.getPaginator())
        .then((response) => ctrl.success.getCategories(response))
        .catch(() => {
          ctrl.initData();
        });
    },

    initData: function () {
      ctrl.setDataForGrid();
    },

    approve: function (requestId, index) {
      ApiService.partners
        .approve(requestId)
        .then((response) => {
          ctrl.model[index].status = response.data.payload.status;
        })
        .catch(() => {
          // noop
        });
    },

    decline: function (requestId, index) {
      ApiService.partners
        .decline(requestId)
        .then((response) => {
          ctrl.model[index].status = response.data.payload.status;
        })
        .catch(() => {
          // noop
        });
    },

    create: function () {
      if (ctrl.selectedCategory && ctrl.selectedCategory.id) {
        $state.go('application.partners.create', { categoryId: ctrl.selectedCategory.id });
      } else {
        $state.go('application.partners.create');
      }
    },

    hasPartners: function () {
      return Array.isArray(ctrl.model) && ctrl.model.length > 0;
    },

    hasCategorized: function () {
      return Array.isArray(ctrl.categorized) && ctrl.categorized.length > 0;
    },

    hasCategories: function () {
      return Array.isArray(ctrl.categories) && ctrl.categories.length > 0;
    },

    process: function (partners) {
      for (let i = 0, ii = partners.length; i < ii; i++) {
        if (partners[i].primaryContact) {
          partners[i].primaryContact.contactName = '';
          if (partners[i].primaryContact.firstName) {
            partners[i].primaryContact.contactName = partners[i].primaryContact.firstName;
          }
          if (partners[i].primaryContact.lastName) {
            partners[i].primaryContact.contactName += ' ' + partners[i].primaryContact.lastName;
          }
        } else {
          console.log('PartnersListController:process !partners[i].primaryContact', partners[i]);
          partners[i].primaryContact = {};
          partners[i].primaryContact.contactName = '';
        }
        for (let j = 0, jj = partners[i].requests.length; j < jj; j++) {
          partners[i][partners[i].requests[j].type] = {
            status: HelpersService.cap(partners[i].requests[j].status),
          };
        }
      }

      return partners;
    },

    processSearch: function (list) {
      return list.map((item) => {
        item.name = item.organizationName;
        return {
          accountCategoryRelation: { categoryName: item.category },
          company: item,
          primaryContact: {
            contactName: item.firstName + ' ' + item.lastName,
          },
          request: {},
          reseller: {
            status: HelpersService.capFirstLetter(item.reseller),
          },
        };
      });
    },

    isOrgSearchDisabled: function () {
      return (
        (ctrl.organizationName.length > 0 && ctrl.organizationName.length < 3) ||
        (ctrl.contactName.length > 0 && ctrl.contactName.length < 3)
      );
    },

    setDataForGrid: function () {
      ctrl.isLoaded = false;

      const props = {
        ...ctrl.gridState.getGridState(),
        category: ctrl.selectedCategory ? ctrl.selectedCategory.name : '',
        organizationName: ctrl.organizationName,
        contactName: ctrl.contactName,
      };

      ApiService.organizations
        .searchOrganizations(props)
        .then((response) => ctrl.success.init(response))
        .catch(() => {
          ctrl.isLoaded = true;
        });
    },

    query: function () {
      $scope.$broadcast('DataGrid::Search', { name: this.accountsListGridName });
    },

    success: {
      init: function (response) {
        ctrl.model = ctrl.processSearch(response.data.payload.list);

        ctrl.categorized = angular.copy(ctrl.model);
        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
        $timeout(() => {
          $scope.$broadcast('DataGrid::ResizeGrid', {
            name: ctrl.gridDefinition.options.gridName,
            datasetLength: ctrl.model.length,
            totalItems: response.data.payload.pagination.count,
          });
        }, 10);
      },

      getCategories: function (response) {
        const category = ViewStateService.getStateProperty('partners.list', 'selectedCategory', '');
        ctrl.categories = response.data.payload.list;

        if (category && category.id) {
          for (let i = 0, ii = ctrl.categories.length; i < ii; i++) {
            if (ctrl.categories[i].id === category.id) {
              ctrl.selectedCategory = ctrl.categories[i];
              break;
            }
          }
        }
        $scope.$on('$destroy', $scope.$watch('partnersList.selectedCategory', ctrl.onSelectedCategoryUpdate));
      },
    },

    onDblClick: function (params) {
      ViewStateService.setState('partners.list', {
        searchQuery: ctrl.searchQuery,
        selectedCategory: ctrl.selectedCategory,
        page: ctrl.gridState.getPropertyValue('page'),
      });

      $state.go('application.partners.detail', { id: params.entity.company.organizationId });
    },

    onSelectedCategoryUpdate: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        ViewStateService.setStateProperty('partners.list', 'page', 0);
        ctrl.setDataForGrid();
      }
    },
  };

  $scope.partnersList = ctrl;
  ctrl.init();
}

export const PartnersListControllerModule = angular
  .module('acme.view.application.partners.list.controllers', [
    uiRouter,
    'ui.bootstrap',
    'ui.grid',
    'ui.grid.autoResize',
    'ui.grid.selection',
    'ui.grid.pagination',
    'ui.grid.resizeColumns',
    'ui.grid.edit',
    'ui.grid.grouping',
    'ui.grid.draggable-rows',
    'ui.grid.moveColumns',
    'ui.grid.saveState',
    'acme.common.service.api',
    'acme.common.service.venue',
    'acme.common.service.helpers',
    'acme.common.service.calendar',
    CartServiceModule,
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    'acme.common.service.viewState',
  ])
  .controller('PartnersListController', PartnersListController).name;
